import React, { Fragment } from "react";
import { Helmet } from 'react-helmet';
import LayoutOne from "../layouts/LayoutOne";
import PageHeader from '../components/PageHeader';
import Seo from '../components/Seo';
import SectionTitle from '../components/SectionTitles/SectionTitle';
import { LinkWithQuery } from '../helpers/LinkWithQuery';

const faqs = [
  {
    question: "Are you a broker? Will you subcontract my move to another moving company?",
    answer: "No, and it's a question worth asking any mover. We are not a broker, and we never hand your move to another company. When you book with us, you get what we are most proud of: extremely professional, highly trained crews who move homes every single day, and our own fleet of state-of-the-art trucks and equipment, maintained to a standard you can see. The crew that packs your home works for Navy Seals Moving, drives our trucks, and stays responsible for your belongings from the first box to the final delivery. One company, one team, start to finish."
  },
  {
    question: "Why is one moving quote so much cheaper than another?",
    answer: "Because not every quote is the same kind of promise, and not every crew does the same job. Many companies win your booking with a fast, low number over the phone. Then moving day arrives, your belongings are seen for the first time, and the price climbs. We work in the opposite order: first a free video walkthrough or in-person visit, so we see exactly what's moving. Then a guaranteed price. The number we quote is the number you pay, as long as what's moving doesn't change. And that price buys a different level of care: experienced crews who do this every day, premium packing materials, and the kind of handling that has kept us at 5 stars across hundreds of reviews for over 20 years. We won't always be the lowest number, but we aim to be the movers you'd hire again. Ask each company one simple question: \"Is this price guaranteed after you've seen my home?\" Our answer is yes."
  },
  {
    question: "Do you move customers from Washington to other states?",
    answer: "Yes. Long-distance moving is what we do. Our trucks leave Washington for destinations all across the country, and wherever you're headed, we can take you there."
  },
  {
    question: "Do you transport cars?",
    answer: "Not ourselves. Vehicle transport is its own specialty, and we'd rather connect you with experts than do it halfway. We work with Reliable Carriers and recommend contacting them directly to arrange your vehicle's transportation."
  },
  {
    question: "How long will delivery take?",
    answer: "It all starts with one date: your First Available Delivery Date, the first day you can meet us at your new home. For moves up to 1,000 miles, we aim to deliver on that date or within about 7 days of it. For longer moves, plan on roughly 7–10 business days from that date. One detail that surprises people: the clock starts when you're ready to receive your belongings, not on pickup day, so if you need time to travel or get your keys, that time doesn't count against your delivery window."
  },
  {
    question: "Do you offer packing services, or do we pack ourselves?",
    answer: "Either way works. Our crews can handle the full packing with premium materials as part of our white-glove service, or you can pack yourself and we take care of loading, transport, and delivery. Many customers choose something in between: we pack the fragile and awkward items, they handle the rest. Just tell us your preference when requesting your estimate."
  },
  {
    question: "Can you store our belongings between homes?",
    answer: "Yes. We operate our own secure, climate-controlled warehouse, so if your new home isn't ready because closing dates don't line up or a remodel is running long, we pick up on your schedule, store everything safely, and deliver when you're ready. One company handles the whole chain, so nothing is handed off between providers."
  },
  {
    question: "Our home has stairs. Is that a problem?",
    answer: "Not at all. Stairs, elevators, and tricky access are everyday work for our crews. Just mention it when you request your estimate (floor number, elevator reservations, a long walk from parking) so we can plan the right crew and equipment, and your quote stays accurate with no surprises."
  }
];

const Faq = () => {
  return (
    <Fragment>
      <Seo
        title="Frequently Asked Questions | Navy Seals Moving"
        description="Answers to the questions we hear most: broker vs. carrier, states we serve, vehicle transport, and how long delivery takes."
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqs.map(f => ({
            "@type": "Question",
            "name": f.question,
            "acceptedAnswer": { "@type": "Answer", "text": f.answer }
          }))
        })}</script>
      </Helmet>
      <LayoutOne>
        <PageHeader
          bgImg={(process.env.PUBLIC_URL + '/assets/images/page_bg.jpg')}
          title="Frequently Asked Questions"
        />
        <section className="about-section">
          <div className="container">
            <div className="row">
              <div className="col-md-10 col-sm-12 about_bottom_warp">
                <div className="about_list faq-list">
                  <SectionTitle title="Your Questions, Answered" />
                  {faqs.map((faq, i) => (
                    <div className="faq-item" key={i}>
                      <h5>{faq.question}</h5>
                      <p>{faq.answer}</p>
                    </div>
                  ))}
                  <p className="faq-more">
                    Have a question we didn't cover? Call us at{' '}
                    <a href="tel:2066366961">(206) 636-6961</a> or use our{' '}
                    <LinkWithQuery to={process.env.PUBLIC_URL + "/contact"}>contact us form</LinkWithQuery>.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </LayoutOne>
    </Fragment>
  );
};

export default Faq;
